import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Heart } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const numbers = [1, 2, 3, 4, 5];

const RSVPForm = () => {
  const [form, setForm] = useState({ full_name: "", attending: "", number_attending: 1, guest_names: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) return toast.error("Please enter your full name.");
    if (!form.attending) return toast.error("Please let us know if you're attending.");
    setSubmitting(true);
    try {
      await api.post("/rsvp", {
        ...form,
        number_attending: form.attending === "yes" ? Number(form.number_attending) : 0,
      });
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white border border-border rounded-md px-4 py-3.5 text-navy placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-dusty transition-shadow";
  const labelCls = "block uppercase tracking-[0.18em] text-xs font-bold text-navy/70 mb-2.5";

  return (
    <AnimatePresence mode="wait">
      {done ? (
        <motion.div
          key="done"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-border p-10 md:p-14 text-center shadow-[0_20px_50px_rgba(38,62,76,0.08)]"
          data-testid="rsvp-confirmation"
        >
          <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-6">
            <Check size={30} className="text-navy" />
          </div>
          <h3 className="font-serif text-3xl md:text-4xl text-navy mb-4">Thank you!</h3>
          <p className="text-navy max-w-md mx-auto leading-relaxed">
            {form.attending === "yes"
              ? "We're thrilled you'll be joining us. Your RSVP has been received — we can't wait to celebrate with you!"
              : "Thank you for letting us know. You'll be missed, and you're in our hearts on our special day."}
          </p>
          <div className="mt-6 inline-flex items-center gap-1.5 text-blush">
            <Heart size={14} className="fill-blush" /> <span className="text-navy/60 text-sm">George & Amy</span>
          </div>
        </motion.div>
      ) : (
        <motion.form
          key="form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-border p-8 md:p-10 shadow-[0_20px_50px_rgba(38,62,76,0.08)] space-y-6"
          data-testid="rsvp-form"
        >
          <div>
            <label className={labelCls} htmlFor="full_name">Full Name *</label>
            <input
              id="full_name"
              type="text"
              value={form.full_name}
              onChange={(e) => update("full_name", e.target.value)}
              placeholder="Your full name"
              className={inputCls}
              data-testid="rsvp-fullname"
              required
            />
          </div>

          <div>
            <span className={labelCls}>Will you be attending? *</span>
            <div className="grid grid-cols-2 gap-3">
              {[{ v: "yes", l: "Joyfully accepts" }, { v: "no", l: "Regretfully declines" }].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => update("attending", opt.v)}
                  className={`rounded-md border px-4 py-4 text-sm transition-colors duration-300 ${
                    form.attending === opt.v
                      ? "bg-navy text-white border-navy"
                      : "bg-white text-navy/80 border-border hover:border-dusty"
                  }`}
                  data-testid={`rsvp-attending-${opt.v}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence>
            {form.attending === "yes" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-6 overflow-hidden"
              >
                <div>
                  <label className={labelCls} htmlFor="number_attending">Number attending</label>
                  <select
                    id="number_attending"
                    value={form.number_attending}
                    onChange={(e) => update("number_attending", e.target.value)}
                    className={inputCls}
                    data-testid="rsvp-number"
                  >
                    {numbers.map((n) => (
                      <option key={n} value={n}>{n} {n === 1 ? "guest" : "guests"}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="guest_names">Names of those attending</label>
                  <textarea
                    id="guest_names"
                    value={form.guest_names}
                    onChange={(e) => update("guest_names", e.target.value)}
                    placeholder="e.g. Jane Doe, John Doe"
                    rows={3}
                    className={inputCls}
                    data-testid="rsvp-guestnames"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-full bg-navy text-white uppercase tracking-[0.18em] text-sm py-4 hover:bg-dusty transition-colors duration-300 disabled:opacity-60"
            data-testid="rsvp-submit"
          >
            {submitting ? "Sending..." : "Send RSVP"}
          </button>
        </motion.form>
      )}
    </AnimatePresence>
  );
};

export default RSVPForm;
